/**
 * CopyMessageButton — small icon button that copies a plain-text response to
 * the clipboard. Used on assistant message bubbles so users can paste AI
 * answers elsewhere without markdown artefacts.
 *
 * On successful copy:
 *   - aria-label flips from "Copy response" to "Copied"
 *   - icon swaps from clipboard to tick
 *   - "Copied!" tooltip text becomes visible
 *   - state reverts automatically after 2 seconds
 *
 * On clipboard rejection (permission denied etc.) the button remains in its
 * initial state and does not throw.
 */

import { useEffect, useRef, useState } from "react";

interface CopyMessageButtonProps {
	/** Plain-text content to place on the clipboard. */
	text: string;
}

/** Clipboard icon — shown in the default state. */
function ClipboardIcon() {
	return (
		<svg
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			aria-hidden="true"
		>
			<rect x="4" y="2.5" width="8" height="11" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
			<rect x="6" y="1" width="4" height="2.5" rx="0.6" fill="currentColor" />
		</svg>
	);
}

/** Tick icon — shown briefly after a successful copy. */
function TickIcon() {
	return (
		<svg
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			aria-hidden="true"
		>
			<path
				d="M3.5 8L6.5 11L12.5 5"
				stroke="currentColor"
				strokeWidth="1.6"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export default function CopyMessageButton({ text }: CopyMessageButtonProps) {
	const [copied, setCopied] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Clear any pending timer on unmount so the state update does not fire
	// against an unmounted component.
	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);

	async function handleClick() {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			if (timerRef.current) clearTimeout(timerRef.current);
			timerRef.current = setTimeout(() => {
				setCopied(false);
				timerRef.current = null;
			}, 2000);
		} catch {
			// Clipboard permission denied or not available — leave the button
			// in its initial state. We deliberately do not surface an error
			// to the user because this is a non-critical convenience action.
		}
	}

	return (
		<button
			type="button"
			data-testid="copy-message-button"
			className={`copy-message-button${copied ? " copy-message-button--copied" : ""}`}
			aria-label={copied ? "Copied" : "Copy response"}
			onClick={handleClick}
		>
			{copied ? <TickIcon /> : <ClipboardIcon />}
			{copied && <span className="copy-message-button-label">Copied!</span>}
		</button>
	);
}
